import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveRun } from "@/features/production/hooks";
import { formatRelativeTime } from "@/lib/display-labels";
import { LineCardTitleBlock } from "./LineCardTitleBlock";
import { LineCardMetrics } from "./LineCardMetrics";
import { LineCardActions } from "./LineCardActions";
import type { LineSummary, MachineDetail } from "@/types/lines.types";

interface Props {
  line: LineSummary;
  expanded: boolean;
  onToggleExpand: () => void;
  onMachineClick: (m: MachineDetail) => void;
  selected?: boolean;
  onToggleSelect?: () => void;
}

export function LineCardHeader({
  line,
  expanded,
  onToggleExpand,
  onMachineClick,
  selected = false,
  onToggleSelect,
}: Props): React.ReactElement {
  const { data: run } = useActiveRun(line.id);

  const pauseEta =
    run?.status === "PAUSED" && run.expectedEnd ? formatRelativeTime(run.expectedEnd) : null;

  const handleToggle = React.useCallback(() => {
    onToggleExpand();
  }, [onToggleExpand]);
  const handleKey = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={expanded ? `Recolher ${line.name}` : `Expandir ${line.name}`}
      onClick={handleToggle}
      onKeyDown={handleKey}
      className={cn(
        "group/header flex cursor-pointer flex-col gap-3 px-[22px] py-[18px]",
        "hover:bg-bg-muted-rd/40",
        "focus-visible:outline-none focus-visible:[box-shadow:inset_0_0_0_2px_var(--brand-600)]",
        "lg:grid lg:items-center lg:gap-7 lg:[grid-template-columns:minmax(260px,1.6fr)_minmax(220px,1fr)_minmax(220px,1fr)_minmax(180px,1fr)_auto]"
      )}
    >
      {}
      <LineCardTitleBlock
        line={line}
        pauseEta={pauseEta}
        selected={selected}
        onToggleSelect={onToggleSelect}
      />

      {}
      <LineCardMetrics line={line} />

      {}
      <div className="flex shrink-0 items-center gap-1 lg:justify-end">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- isolador de eventos para os botões filhos */}
        <div onClick={(e) => e.stopPropagation()}>
          <LineCardActions line={line} onMachineClick={onMachineClick} />
        </div>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "h-3.5 w-3.5 text-text-tertiary transition-transform",
            expanded && "rotate-180"
          )}
        />
      </div>
    </div>
  );
}
